import { getOrangeBookDataFromDb, getOrangeBookMetadataFromDb, saveOrangeBookToDb } from "store/drugDb";
import { OrangeData, OBLatestVersion, APIVersion, compareOBVersions, setOBLatestVersion, setOrangeData, setAPIVersion } from './lookupDrugs';
import { checkOnline, getAPI, getJson } from "helpers/apiHelpers";
import { getOnline } from "store/storeValtio";

const API_VERSION_URL = "/OrangeBook/version";
const API_JSON_URL = "/OrangeBook/json";
const LOCAL_FALLBACK_URL = "/OBData.json";

let initializationPromise = null;

/**
 * Call once after IndexedDB has been opened.
 *
 * Multiple callers can safely call this. They will share the same
 * initialization promise.
 */
export function initializeOrangeBook() {
  if (!initializationPromise) {
    initializationPromise = initializeOrangeBookInternal();
  }

  return initializationPromise;
}

async function initializeOrangeBookInternal() {
  // First preference: locally cached IndexedDB data.
  const [storedData, storedMetadata] = await Promise.all([
    getOrangeBookDataFromDb(),
    getOrangeBookMetadataFromDb()
  ]);

  if (Array.isArray(storedData) && storedData.length > 0) {
    setOrangeData(storedData);
    setOBLatestVersion(storedMetadata?.version ?? null);

    if (checkOnline()) {
      // Do not delay startup while checking the server.
      checkForOrangeBookUpdate().catch(error => {
        console.warn("Orange Book update check failed:", error);
      });
    }

    return OrangeData;
  }

  // First installation or database upgrade:
  // load the JSON packaged separately with the website.
  const localPackage = await getJson(LOCAL_FALLBACK_URL);
  setOrangeBookFromPackage(localPackage);

  await saveOrangeBookToDb(OrangeData, {
    version: OBLatestVersion,
    source: LOCAL_FALLBACK_URL
  });

  if (checkOnline) {
    // Check for a newer server copy without delaying availability.
    checkForOrangeBookUpdate().catch(error => {
      console.warn("Orange Book update check failed:", error);
    });
  }

  return OrangeData;
}

function setOrangeBookFromPackage(packageData) {
  if (!Array.isArray(packageData) || packageData.length === 0) {
    throw new Error("Orange Book package is empty or invalid");
  }

  const root = packageData[0];

  if (!root?.meta?.last_updated) {
    throw new Error(
      "Orange Book package has no meta.last_updated value"
    );
  }

  if (!Array.isArray(root.data)) {
    throw new Error("Orange Book package has no data array");
  }

  setOBLatestVersion(root.meta.last_updated);
  setOrangeData(root.data);
}

// async function fetchJson(url, options = {}) {
//   const response = await fetch(url, {
//     cache: "no-store",
//     ...options
//   });

//   if (!response.ok) {
//     console.log(response)
//     throw new Error(
//       `Request failed for ${url}: ${response.status}`
//     );
//   }

//   return await response.json();
// }

export async function getLatestOBVersion() {
  if (APIVersion) {
    return APIVersion;
  }

  if (!checkOnline()) return "Unknown";

  const apiJSON = await getAPI(API_VERSION_URL);

  if (!apiJSON?.last_updated) {
    throw new Error(
      "Orange Book API version response is invalid"
    );
  }

  setAPIVersion(apiJSON.last_updated);
  return APIVersion;
}

export async function getOrangeBookStatus() {
  const localMetadata = await getOrangeBookMetadataFromDb();
  var remoteError = "";

  if (checkOnline()) {
    try {
      const serverMetadata = await fetchServerMetadata();

      return {
        localMetadata,
        serverMetadata,
        updateAvailable:
          !localMetadata ||
          localMetadata.version !== serverMetadata.version,
        online: true
      };
    } catch (error) {
      remoteError = error;
      console.warn("Orange Book version check failed:", error);
    }
  }

  return {
    localMetadata,
    serverMetadata: null,
    updateAvailable: false,
    online: false,
    error: remoteError
  };
}

export async function getOrangeBookData() {
  return await getOrangeBookDataFromDb();
}

async function fetchServerMetadata() {
  const response = await getAPI(API_VERSION_URL);
  if (response == null) return null;

  if (!response.version) {
    throw new Error(
      "Orange Book metadata does not contain a version"
    );
  }

  // if (!response.url) {
  //   throw new Error(
  //     "Orange Book metadata does not contain a data URL"
  //   );
  // }

  return response;
}

export async function downloadOrangeBook() {

  const response = await getAPI(API_JSON_URL);

  if (response == null) {
    throw new Error(
      `Orange Book download failed: ${response.status}`
    );
  }

  const data = response.data;
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Downloaded Orange Book data is invalid");
  }

  const meta = response.meta;
  if (
    Number.isInteger(meta.length) &&
    data.length !== meta.length
  ) {
    throw new Error(
      `Orange Book record count mismatch: expected ` +
      `${meta.length}, received ${data.length}`
    );
  }

  return data;
}

export function normalizeVersion(version) {
  if (!version || typeof version !== "string") {
    return null;
  }

  if (version === "Unknown") return null;

  // Handles "orange_book_2026_08"
  let match = version.match(/(\d{4})[_-](\d{1,2})$/);

  // Handles "2026-08"
  if (!match) {
    match = version.match(/^(\d{4})-(\d{1,2})$/);
  }

  // Handles "2026-08=01"
  if (!match) {
    match = version.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  }

  if (!match) {
    throw new Error(
      `Unrecognized Orange Book version: ${version}`
    );
  }

  const year = Number(match[1]);
  const month = Number(match[2]);

  return {
    year,
    month,
    comparable: year * 100 + month,
    canonical:
      `${year}-${String(month).padStart(2, "0")}`
  };
}

export async function checkForOrangeBookUpdate() {
  await initializeOrangeBook();

  const comparison = await compareOBVersions();

  if (comparison <= 0) {
    return {
      updated: false,
      version: OBLatestVersion,
      data: OrangeData
    };
  }

  return await updateOrangeBook();
}

export async function updateOrangeBook() {
  const apiPackage = await getAPI(API_JSON_URL);

  setOrangeBookFromPackage(apiPackage);

  const metadata = {
    version: OBLatestVersion,
    source: API_JSON_URL,
    downloadedAt: new Date().toISOString(),
    recordCount: OrangeData.length
  };

  await saveOrangeBookToDb(
    OrangeData,
    metadata
  );

  setAPIVersion(OBLatestVersion);

  return {
    updated: true,
    version: OBLatestVersion,
    data: OrangeData
  };
}

export async function setOrangeBookFromJson() {
  const apiPackage = await getAPI(LOCAL_FALLBACK_URL);

  setOrangeBookFromPackage(apiPackage);

  const metadata = {
    version: OBLatestVersion,
    source: API_JSON_URL,
    downloadedAt: new Date().toISOString(),
    recordCount: OrangeData.length
  };

  await saveOrangeBookToDb(
    OrangeData,
    metadata
  );

  setAPIVersion(OBLatestVersion);

  return {
    updated: true,
    version: OBLatestVersion,
    data: OrangeData
  };
}

